/**
 * The feature-request board (PLAN-SUPPORT).
 *
 * ## It works signed out, and that is the point
 *
 * There is no route guard above this page and nothing here is hidden from a
 * stranger: the same rows, the same counts, the same statuses. The session is
 * read in exactly one place, {@link VoteButton}, and it decides one thing — what
 * pressing the vote does. A guest gets a button that works and a sentence saying
 * what voting needs, rather than a disabled control or a redirect. That is the
 * posture the gallery's thumb sets, and it is the same posture for the same
 * reason: a count everybody can read is not a secret, and a control that renders
 * its own pressed state back has to know whose state it is.
 *
 * ## Nobody's name is on anything here
 *
 * No byline, no avatar, no "posted by". The wire does not carry one
 * (`board/protocol.ts`), so this file has nothing to leave out. It is worth
 * saying anyway, because the shape of a list like this invites somebody to add
 * one later, and the whole argument for the board is that asking for something
 * should cost nothing socially.
 *
 * ## "Yours" is above the list, and it is the only place authorship shows
 *
 * {@link YourRequests} renders for a signed-in brewer who has asked for
 * something, and it renders **their own** rows only — the server decides that
 * from the session (`worker/board.ts`), and this component could not ask about
 * anybody else's if it wanted to. It is above the public list rather than a tab
 * beside it because it is short, it is the half of the page that changed since
 * the last visit, and the thing under it is what somebody came to read.
 *
 * It is also the one surface that can show a **hidden** request, with the reason
 * it came off the board. That is the whole reason the route exists: a request
 * that disappears with no account of why reads as censorship even when it was a
 * duplicate, and there was previously nowhere to put the sentence an operator is
 * already required to write.
 *
 * ## Four sections, and why the order is the one it is
 *
 * The board was one list ordered by votes, and that list got worse as the
 * product got better: a shipped request keeps every vote that carried it, so it
 * holds the top row forever, and a reader arriving with either of the two
 * questions a board is for — what is coming, and what can I still push for — had
 * to answer them by reading badges down a mixed list.
 *
 * So: Planned, then Open, then Shipped and Declined shut at the bottom. Planned
 * leads because it is the answer to the first question and because it is short.
 * Open follows because it is what somebody came to read and the only section
 * their vote can change. The last two are *decided* — nothing a reader does
 * moves them, and they only ever grow — which is what makes them the two that
 * fold away, and {@link BoardSection} is where that lives.
 *
 * Each section is its own keyset query against `?status=` rather than one page
 * grouped after the fact, because a page of twenty-four sorted by votes can be
 * entirely shipped: grouping it would print "Open (0)" over a board with forty
 * open requests on it. The headings come from a third read
 * ({@link useBoardCounts}) for the reason a collapsed section needs a number
 * before anybody has asked for its rows.
 *
 * **`open` is every undecided status, not an equality.** A status this build has
 * never heard of is one a later build wrote, and the row component renders it
 * rather than falling over; sections are a partition, so one of them has to
 * catch everything, or tomorrow's request goes missing from today's page. The
 * Worker defines it the same way and for the same reason (`worker/board.ts`).
 *
 * ## The form is a row, not a dialog
 *
 * "Ask for something" expands in place, the way `NewTicketRow` does in the
 * support section. A dialog would put a modal in front of the list somebody is
 * reading to check whether their idea is already on it — which is the one thing
 * the top-first order exists to encourage.
 *
 * ## Loading without flashing
 *
 * Three states, and only the first of them is allowed to take the board away
 * (PLAN-LOADING D-LD1). A **first read** has nothing worth keeping and no counts
 * to draw sections from, so the page draws {@link BoardSkeleton} — rows where
 * rows will be, rather than a sentence standing where the list goes, which would
 * change the shape of the page when it went. A **re-read** — a new sort, a
 * search that has settled, a section just opened — keeps the rows that are there
 * and dims them, because the list a reader is looking at is a better answer to
 * "what has been asked for" than the absence of one, right up until a newer
 * answer lands. A **failure** keeps them too, says so in one line above the
 * sections, and offers to ask again; the nothing-anywhere case is the only one
 * that can say nothing else, and even it offers the retry.
 *
 * The four sections are four queries but one page: they ask the same server at
 * the same moment, so the stale line, the "updating" announcement and Try again
 * are the page's rather than each section's — one dropped connection deserves
 * one sentence, not four.
 *
 * The search is debounced here rather than inside {@link useBoard}
 * (`lib/use-debounced-value.ts`) because the two are different jobs: the hook
 * answers a query, and deciding when a keystroke has *become* a query is the
 * box's business. A hook that debounced its own arguments would also debounce
 * the sort, which is finished the moment it is pressed.
 */

import { ChevronRightIcon, ChevronUpIcon, SearchIcon } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useSession } from '@/auth/session-context';
import type { BoardState, RequestFailure, VoteFailure, VoteResult } from '@/board/client';
import { submitFeatureRequest, useBoard, useBoardCounts, useOwnRequests } from '@/board/client';
import type {
  BoardSort,
  FeatureRequestCard,
  FeatureRequestStatus,
  OwnRequest,
} from '@/board/protocol';
import {
  BOARD_PAGE_SIZE,
  FEATURE_REQUEST_STATUS_LABELS,
  parseBoardSort,
  REQUEST_BODY_MAX_LENGTH,
  REQUEST_TITLE_MAX_LENGTH,
} from '@/board/protocol';
import { Skeleton } from '@/components/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { userContent } from '@/lib/posthog';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { cn } from '@/lib/utils';

/**
 * Where a guest is pointed. A plain path, and a plain anchor at the call site.
 *
 * `/signin` is written literally at the app's other sign-in links too; there is
 * no constant to import, and this file deliberately has no router dependency.
 */
const SIGN_IN_PATH = '/signin';

/**
 * How long the search box waits before it counts as a question.
 *
 * Long enough that a word typed at speed is one question rather than six, short
 * enough that somebody who has stopped typing does not notice they are waiting.
 */
const SEARCH_DEBOUNCE_MS = 250;

/** The list the rows sit in, and the shape the skeleton borrows from it. */
const BOARD_LIST = 'flex flex-col divide-y divide-border';

/** How many placeholder rows stand in for a list whose size nothing knows yet. */
const FIRST_PAGE_ROWS = 5;

export function FeatureBoard() {
  const { user } = useSession();
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<BoardSort>('top');
  const [composing, setComposing] = useState(false);
  const [choices, setChoices] = useState<Readonly<Partial<Record<FeatureRequestStatus, boolean>>>>(
    {},
  );

  // The box keeps every keystroke; the board is asked about the ones that
  // stopped. See "Loading without flashing" in the header — the sort goes
  // straight through, because a press is already a decision.
  const q = useDebouncedValue(search, SEARCH_DEBOUNCE_MS).trim();
  const searching = q !== '';
  const tally = useBoardCounts(q);
  const mine = useOwnRequests(user !== null);

  /**
   * Whether a collapsible section is open: what somebody chose, or the default.
   *
   * The default is the half worth arguing for. The search box exists to stop
   * duplicates (`board/protocol.ts` says so of `q` searching titles only), and
   * the commonest duplicate is a request for something that already shipped.
   * Somebody typing "keg inventory" is answered by being shown that it shipped;
   * a folded row holding that answer would make the search worse than no search.
   *
   * A *choice* rather than a set of open sections, because the two rules
   * otherwise fight: with a set, pressing the header of a section the search had
   * opened would add it to the set and hold it open — the one press that means
   * "close this" being the one that could not. An explicit `false` beats the
   * default; clearing the search leaves nothing to override and the default
   * comes back.
   */
  const showing = useCallback(
    (status: FeatureRequestStatus, count: number) => choices[status] ?? (searching && count > 0),
    [choices, searching],
  );

  const planned = useBoard({ q, sort, status: 'planned' });
  const open = useBoard({ q, sort, status: 'open' });
  const shipped = useBoard({
    q,
    sort,
    status: 'shipped',
    enabled: showing('shipped', tally.counts.shipped),
  });
  const declined = useBoard({
    q,
    sort,
    status: 'declined',
    enabled: showing('declined', tally.counts.declined),
  });

  const toggle = useCallback((status: FeatureRequestStatus, open: boolean) => {
    setChoices((current) => ({ ...current, [status]: !open }));
  }, []);

  /**
   * The four sections as one thing, for the three things the page says on their
   * behalf (see "Loading without flashing" in the header).
   *
   * They ask the same server at the same moment, so a dropped connection is one
   * fact about the page rather than four: one stale line above the lists rather
   * than one over each, one announcement that the board is updating, and a Try
   * again that asks again for every section that could not be reached — the two
   * that failed with nothing to keep included, or they would stay failed behind
   * a sentence that is not about them.
   *
   * The line shows whenever any section failed, whether or not it had rows to
   * keep: a heading reading "Shipped (2)" over an empty list is a failure too,
   * and saying nothing about it leaves the reader to guess. `kept` only picks
   * the wording — "this is what we had" is true only when something was.
   */
  const sections = [planned, open, shipped, declined];
  const failing = sections.filter((section) => section.failed);
  const kept = failing.some((section) => section.requests.length > 0);
  const refreshing = sections.some((section) => section.loading && section.requests.length > 0);
  const retry = () => {
    for (const section of failing) section.refresh();
  };

  /**
   * Nothing landed anywhere yet, which is the one state allowed to take the
   * board away: there is nothing to keep, and the sections cannot draw
   * themselves until the counts say how big they are.
   */
  const firstLoad =
    open.loading && planned.loading && open.requests.length === 0 && planned.requests.length === 0;

  /**
   * Nothing anywhere, as opposed to nothing in the section being looked at.
   *
   * The counts have to have landed before this can be said. Without that check a
   * board whose open list comes back empty a moment before the counts arrive
   * flashes "nobody has asked for anything yet" at somebody looking at a page
   * with twelve shipped requests on it.
   */
  const nothingAnywhere =
    !open.loading &&
    !planned.loading &&
    !tally.loading &&
    open.requests.length === 0 &&
    planned.requests.length === 0 &&
    tally.counts.shipped === 0 &&
    tally.counts.declined === 0;

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative min-w-0 flex-1">
          <SearchIcon
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            placeholder="Search requests…"
            aria-label="Search requests"
            className="pl-8"
          />
        </div>

        <Select value={sort} onValueChange={(value) => setSort(parseBoardSort(value))}>
          <SelectTrigger aria-label="Sort" className="w-full sm:w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="top">Most wanted</SelectItem>
            <SelectItem value="newest">Newest</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <NewRequestRow
        open={composing}
        onOpenChange={setComposing}
        onPosted={() => {
          setComposing(false);
          // A new request is open by definition, so that is the one list that
          // has to be re-read — but the heading above it counts too, and a
          // heading one behind its own list is the sort of thing nobody reports
          // and everybody notices.
          open.refresh();
          tally.refresh();
          // Two lists hold the request that was just posted, and both are the
          // server's answer rather than a local copy of the form's contents.
          mine.refresh();
        }}
      />

      <YourRequests requests={mine.requests} />

      {firstLoad ? (
        <BoardSkeleton />
      ) : nothingAnywhere ? (
        <p data-testid="board-empty" className="py-10 text-center text-muted-foreground text-sm">
          {/* Any section, not only the open one: a board whose open list came
              back honestly empty while planned never arrived has not said
              anything about itself yet, and saying "nobody has asked for
              anything" for it would be the fact the failure is not. */}
          {failing.length > 0 ? (
            <>
              We could not reach the board. Check your connection and try again.{' '}
              <Button type="button" variant="outline" size="sm" onClick={retry}>
                Try again
              </Button>
            </>
          ) : !searching ? (
            'Nobody has asked for anything yet. You could be first.'
          ) : (
            'Nothing here matches that yet.'
          )}
        </p>
      ) : (
        <>
          {failing.length > 0 && (
            <p
              role="status"
              data-testid="board-stale"
              className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center text-muted-foreground text-sm"
            >
              {kept
                ? "We couldn't reach the board, so you're seeing what we loaded last time."
                : 'We could not reach the board.'}{' '}
              <Button type="button" variant="outline" size="sm" onClick={retry}>
                Try again
              </Button>
            </p>
          )}

          {refreshing && (
            <p className="sr-only" role="status">
              Updating the board…
            </p>
          )}

          {/* Planned first — it is short, and it is the answer to the question
              somebody arrives with. Open next, because it is what they came to
              read and the only section they can change by voting. The two
              decided ones sit at the bottom, shut. */}
          <BoardSection status="planned" count={tally.counts.planned} board={planned} />
          <BoardSection status="open" count={tally.counts.open} board={open} />
          <BoardSection
            status="shipped"
            count={tally.counts.shipped}
            board={shipped}
            expanded={showing('shipped', tally.counts.shipped)}
            onToggle={(open) => toggle('shipped', open)}
          />
          <BoardSection
            status="declined"
            count={tally.counts.declined}
            board={declined}
            expanded={showing('declined', tally.counts.declined)}
            onToggle={(open) => toggle('declined', open)}
          />
        </>
      )}
    </div>
  );
}

/**
 * The shape of a list of requests, while the list is on its way.
 *
 * Drawn by the page when nothing has landed anywhere, and by a section whose own
 * rows have not. One or the other, never both: a page with nothing to keep draws
 * no sections at all, so the reader is told "opening the board" once.
 *
 * `rows` is what the section knows and the page does not. A heading that already
 * says "Shipped (2)" has promised two rows, and opening it onto five that settle
 * back to two would reflow the page under the press that opened it — the shape
 * of the answer means its size too (D-LD4). The page has no such promise to keep
 * (its counts have not landed either, or it would be drawing sections), so it
 * takes the default: a screenful of rows, which is what a first page usually is.
 */
function BoardSkeleton({ rows = FIRST_PAGE_ROWS }: { readonly rows?: number }) {
  return (
    <Skeleton
      rows={rows}
      label="Opening the board…"
      className={BOARD_LIST}
      testId="board-skeleton"
    />
  );
}

/**
 * One status: its heading, its rows, and its own "Show more".
 *
 * Every section pages independently, because they are four keyset queries rather
 * than one list drawn with rules across it. That is the whole reason the server
 * takes a status (`worker/board.ts`) instead of the page grouping a page it
 * already has: a page of twenty-four sorted by votes could be entirely shipped,
 * and grouping it would print "Open (0)" over a board with forty open requests
 * on it.
 *
 * **A section with nothing in it renders nothing at all** — no heading, no empty
 * box. It is the rule {@link YourRequests} already follows and it matters more
 * here, because three of the four are empty on a young board and a page of
 * headings apologising for themselves is what the sections were supposed to fix.
 *
 * `onToggle` is what makes a section collapsible. The two that have it —
 * shipped and declined — are the two that are decided: nothing a reader does
 * changes them, they only grow, and a shipped request keeps every vote that
 * carried it, so left in the main list they would hold the top rows forever.
 */
function BoardSection({
  status,
  count,
  board,
  expanded,
  onToggle,
}: {
  readonly status: FeatureRequestStatus;
  readonly count: number;
  readonly board: BoardState;
  readonly expanded?: boolean;
  /** Called with the state the section is in now, so the caller records the opposite. */
  readonly onToggle?: (expanded: boolean) => void;
}) {
  const collapsible = onToggle !== undefined;
  // The two halves of `loading`, which want opposite things: nothing to keep, so
  // draw the shape of the answer; something to keep, so keep it and dim it while
  // the newer answer is on its way.
  const firstLoad = board.loading && board.requests.length === 0;
  const refreshing = board.loading && board.requests.length > 0;
  // The count answers for a collapsed section, whose rows have not been asked
  // for yet; the rows answer for an open one, whose count may be a moment behind
  // a post. Either being non-zero means there is a section here — and a count of
  // nothing means there is nothing to draw the shape of either, which is why a
  // section that is loading is not an exception to this.
  if (count === 0 && board.requests.length === 0) return null;

  const label = FEATURE_REQUEST_STATUS_LABELS[status];
  const listId = `board-section-${status}-list`;
  const heading = (
    <>
      <span className="font-medium text-sm">{label}</span>
      <span className="font-mono text-muted-foreground text-xs tabular-nums">{count}</span>
    </>
  );

  return (
    <section data-testid={`board-section-${status}`} className="flex min-w-0 flex-col gap-2">
      {collapsible ? (
        <button
          type="button"
          data-testid={`board-toggle-${status}`}
          onClick={() => onToggle(expanded === true)}
          aria-expanded={expanded}
          aria-controls={listId}
          className="-mx-2 flex items-center gap-2 rounded-md px-2 py-1 text-left hover:bg-muted/60"
        >
          <ChevronRightIcon
            aria-hidden="true"
            className={cn('size-4 shrink-0 transition-transform', expanded === true && 'rotate-90')}
          />
          {heading}
        </button>
      ) : (
        <h3 className="flex items-center gap-2 border-border border-b pb-1">{heading}</h3>
      )}

      {(!collapsible || expanded === true) && (
        <>
          {firstLoad ? (
            // Capped at a page: a section of forty asks for twenty-four of them
            // first, and forty placeholders would promise a page nothing is
            // fetching.
            <BoardSkeleton rows={Math.min(count, BOARD_PAGE_SIZE)} />
          ) : (
            <ul
              id={listId}
              data-testid={status === 'open' ? 'board-list' : `board-section-${status}-rows`}
              aria-busy={refreshing}
              className={cn(BOARD_LIST, refreshing && 'opacity-60 transition-opacity')}
            >
              {board.requests.map((request) => (
                <RequestRow key={request.id} request={request} onToggleVote={board.toggleVote} />
              ))}
            </ul>
          )}

          {board.hasMore && (
            <div className="flex justify-center">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={board.loadingMore}
                onClick={board.loadMore}
              >
                {board.loadingMore ? 'Loading…' : 'Show more'}
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/**
 * The requests this brewer has asked for, with what became of each.
 *
 * Renders nothing at all when there are none, which is most people most of the
 * time: a heading over an empty box on a page whose point is the list below it
 * would be a section explaining its own absence. Nothing renders while the fetch
 * is in flight either, for the same reason — the section appears when it has
 * something to say.
 *
 * The rows are deliberately not {@link RequestRow}s. There is no vote control
 * (voting for your own request from a list of your own requests is a strange
 * thing to offer twice), no body, and no expander: this is a status list, and
 * the request itself is on the board below with everything else. What it adds
 * that no other surface can is the hidden row and its reason.
 */
function YourRequests({ requests }: { readonly requests: readonly OwnRequest[] }) {
  if (requests.length === 0) return null;

  return (
    <section data-testid="board-mine" className="flex flex-col gap-2">
      <h3 className="font-medium text-sm">Yours</h3>
      <ul className="flex flex-col divide-y divide-border rounded-lg border border-border px-3">
        {requests.map((request) => (
          <li
            key={request.id}
            data-testid="board-mine-row"
            className="flex min-w-0 flex-col gap-1 py-2"
          >
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <span {...userContent} className="min-w-0 flex-1 text-sm">
                {request.title}
              </span>
              <StatusBadge status={request.status} />
              <span className="font-mono text-muted-foreground text-xs tabular-nums">
                {request.voteCount} {request.voteCount === 1 ? 'vote' : 'votes'}
              </span>
            </div>

            {request.adminNote !== null && (
              <p className="text-foreground/80 text-xs">{request.adminNote}</p>
            )}

            {/* The sentence that could not be written until this section existed.
                The reason is required of the operator who hides a request
                (`worker/admin/routes.ts`), so a hidden row always has one. */}
            {request.hidden && (
              <p data-testid="board-mine-hidden" className="text-muted-foreground text-xs">
                We took this off the board
                {request.hiddenReason === null ? '.' : `: ${request.hiddenReason}`}
              </p>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * One request: what was asked, where it stands, and the vote.
 *
 * The body is clamped to three lines with a control that opens it, rather than
 * truncated with an ellipsis. A request is an argument, and an argument cut off
 * mid-sentence with no way to finish reading it is worse than a long row.
 */
function RequestRow({
  request,
  onToggleVote,
}: {
  readonly request: FeatureRequestCard;
  readonly onToggleVote: (requestId: string) => Promise<VoteResult>;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <li data-testid="board-row" className="flex min-w-0 items-start gap-3 py-3">
      <VoteButton request={request} onToggleVote={onToggleVote} />

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span {...userContent} className="min-w-0 font-medium text-sm">
            {request.title}
          </span>
          <StatusBadge status={request.status} />
        </div>

        <p
          {...userContent}
          data-testid="board-row-body"
          className={cn(
            'whitespace-pre-line text-muted-foreground text-sm',
            !expanded && 'line-clamp-3',
          )}
        >
          {request.body}
        </p>

        <button
          type="button"
          data-testid="board-row-expand"
          onClick={() => setExpanded((open) => !open)}
          className="flex w-fit items-center gap-1 text-muted-foreground text-xs underline-offset-2 hover:text-foreground hover:underline"
        >
          {expanded ? 'Show less' : 'Read it all'}
        </button>

        {request.adminNote !== null && (
          <p data-testid="board-row-note" className="text-foreground/80 text-xs">
            {request.adminNote}
          </p>
        )}
      </div>
    </li>
  );
}

/**
 * The status, always shown and muted while it is `open`.
 *
 * Always, rather than only once somebody has decided something: a row with no
 * badge beside rows that have one reads as a row somebody forgot, and "Open" is
 * the honest word for a request nobody has ruled on. An unfamiliar status from a
 * newer Worker renders verbatim rather than crashing, which is what the wire
 * type's `| string` is for.
 */
function StatusBadge({ status }: { readonly status: string }) {
  const label = FEATURE_REQUEST_STATUS_LABELS[status as keyof typeof FEATURE_REQUEST_STATUS_LABELS];
  return (
    <Badge variant={status === 'open' ? 'muted' : 'default'} data-testid="board-row-status">
      {label ?? status}
    </Badge>
  );
}

/**
 * The vote, and the sentence a guest gets instead of one.
 *
 * `aria-pressed` rather than a checkbox: it is a toggle on a control that also
 * carries a number, and the number is the label's other half. The guest branch
 * never reaches the network — the button is real, it does something, and what it
 * does is say what voting needs.
 *
 * `user !== null` rather than `status === 'authenticated'` is the identity
 * question this codebase asks everywhere (`auth/session-context.tsx`): an offline
 * session belongs to somebody, so it gets the ordinary press and, if the server
 * cannot be reached, the ordinary "that did not go through" sentence rather than
 * being told to sign in to an account it already has.
 */
function VoteButton({
  request,
  onToggleVote,
}: {
  readonly request: FeatureRequestCard;
  readonly onToggleVote: (requestId: string) => Promise<VoteResult>;
}) {
  const { user } = useSession();
  const [notice, setNotice] = useState<VoteFailure | null>(null);
  const [busy, setBusy] = useState(false);

  const press = useCallback(async () => {
    setNotice(null);
    if (user === null) {
      setNotice('unauthorized');
      return;
    }
    setBusy(true);
    const result = await onToggleVote(request.id);
    setBusy(false);
    if (!result.ok) setNotice(result.reason);
  }, [user, request.id, onToggleVote]);

  return (
    <div className="flex w-24 shrink-0 flex-col items-center gap-1">
      <button
        type="button"
        disabled={busy}
        aria-pressed={request.votedByMe}
        aria-label={`${request.votedByMe ? 'Take back your vote for' : 'Vote for'} ${request.title}`}
        data-testid="board-vote"
        onClick={() => void press()}
        className={cn(
          'flex w-14 flex-col items-center rounded-md border border-border px-2 py-1 transition-colors hover:border-foreground/30 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:opacity-60',
          request.votedByMe && 'border-foreground/40 bg-muted text-foreground',
        )}
      >
        <ChevronUpIcon aria-hidden="true" className="size-4" />
        <span className="font-mono text-sm tabular-nums">{request.voteCount}</span>
      </button>

      {notice !== null && (
        <p role="alert" className="w-40 text-[11px] text-muted-foreground">
          {notice === 'unauthorized' ? (
            <>
              Voting needs an account.{' '}
              <a href={SIGN_IN_PATH} className="font-medium underline underline-offset-2">
                Sign in
              </a>{' '}
              and your vote is counted.
            </>
          ) : (
            VOTE_FAILURE_MESSAGES[notice]
          )}
        </p>
      )}
    </div>
  );
}

/**
 * What a refused vote says. Stable codes in, sentences out.
 *
 * `unauthorized` is not here: it is the one that carries a link, so it is written
 * as markup above. The other two are things that happen to a page left open,
 * which is why neither blames the reader.
 */
const VOTE_FAILURE_MESSAGES: Readonly<Record<Exclude<VoteFailure, 'unauthorized'>, string>> = {
  'not-found': 'This request is no longer on the board.',
  unavailable: 'That did not reach the server. Check your connection and try again.',
};

/**
 * The form that asks for something.
 *
 * Collapsed until somebody presses the button, because the list is what the page
 * is for and a form sitting open above it says otherwise. Signed out it is a
 * sentence rather than a disabled form: there is nowhere to put a request
 * without an account, and a form that cannot be sent is a worse answer than the
 * reason it cannot.
 */
function NewRequestRow({
  open,
  onOpenChange,
  onPosted,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onPosted: () => void;
}) {
  const { user } = useSession();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<RequestFailure | null>(null);

  if (user === null) {
    return (
      <p className="rounded-lg border border-border border-dashed px-4 py-3 text-muted-foreground text-sm">
        Asking for something needs an account.{' '}
        <a href={SIGN_IN_PATH} className="font-medium text-foreground underline underline-offset-2">
          Sign in
        </a>{' '}
        and your request goes on this list.
      </p>
    );
  }

  if (!open) {
    return (
      <div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="board-new"
          onClick={() => onOpenChange(true)}
        >
          Ask for something
        </Button>
      </div>
    );
  }

  const ready = title.trim() !== '' && body.trim() !== '' && !busy;

  async function send(): Promise<void> {
    if (!ready) return;
    setBusy(true);
    setRefusal(null);
    const result = await submitFeatureRequest(title.trim(), body.trim());
    setBusy(false);
    if (!result.ok) {
      setRefusal(result.reason);
      return;
    }
    setTitle('');
    setBody('');
    onPosted();
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border px-4 py-3">
      <div>
        <p className="font-medium text-sm">Ask for something</p>
        <p className="mt-0.5 text-muted-foreground text-xs">
          It goes on the board straight away, with no name attached to it. Other brewers vote, we
          set the status, and you can always ask us privately instead.
        </p>
      </div>

      <Input
        value={title}
        aria-label="Title"
        placeholder="One line: what would you like?"
        maxLength={REQUEST_TITLE_MAX_LENGTH}
        data-testid="board-title"
        className="h-8"
        onChange={(event) => setTitle(event.currentTarget.value)}
      />
      <Textarea
        value={body}
        aria-label="Details"
        placeholder="What you are trying to do, and why the way you do it now gets in the way."
        maxLength={REQUEST_BODY_MAX_LENGTH}
        data-testid="board-body"
        onChange={(event) => setBody(event.currentTarget.value)}
      />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-mono text-muted-foreground text-xs tabular-nums">
          {title.trim().length}/{REQUEST_TITLE_MAX_LENGTH} · {body.trim().length}/
          {REQUEST_BODY_MAX_LENGTH}
        </span>
        <span className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setRefusal(null);
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!ready}
            data-testid="board-send"
            onClick={() => void send()}
          >
            {busy ? 'Sending…' : 'Post it'}
          </Button>
        </span>
      </div>

      {refusal !== null && (
        <p role="alert" data-testid="board-refusal" className="text-destructive text-xs">
          {REQUEST_FAILURE_MESSAGES[refusal]}
        </p>
      )}
    </div>
  );
}

/**
 * One sentence per refusal, and each one says what to do next.
 *
 * The rate-limit sentence deliberately does not print a countdown. The server
 * sends `Retry-After` and the honest number is "when this hour ends", which is a
 * clock somebody would then watch; "a little while" is the same fact without the
 * invitation.
 */
const REQUEST_FAILURE_MESSAGES: Readonly<Record<RequestFailure, string>> = {
  invalid: 'A request needs a title of 80 characters or fewer and a few words about it.',
  'too-many-open':
    'You already have ten open requests. We read them; give us a chance to catch up.',
  'rate-limited': 'That is five requests inside an hour. Try again in a little while.',
  unavailable: 'That did not reach the server. Check your connection and try again.',
};
